// Historial de eventos por DID / ANI — Fase 4 del Diseno-log-por-ANI.md.
// Lee /v1/ani/{ani}/historial, que sale de la vista netvoice.ani_historial: unifica los
// eventos propios (registro, perdida, alcanzabilidad, llamada fallida) con los bloqueos del
// AGI que ya se registraban desde agosto (permisos, rutas, saldo, antifraude).
import React, { useState, useEffect, useCallback } from "react";
import api from "../api";

// Color por familia de evento. Se deriva del `tipo`, no de la etiqueta, para que un cambio
// de texto no rompa el color.
function colorDe(tipo) {
  if (tipo === "REGISTRO" || tipo === "REGISTRO_RECUPERADO" || tipo === "ALCANZABLE") return "#1e8e3e";
  if (tipo === "REGISTRO_PERDIDO" || tipo === "INALCANZABLE") return "#c5221f";
  if (tipo === "LLAMADA_FALLIDA") return "#e37400";
  if (tipo === "SIN_MEDICION") return "#80868b";
  return "#1a73e8"; // bloqueos del AGI
}

export default function HistorialANI() {
  const [ani, setAni] = useState("");
  const [buscado, setBuscado] = useState("");
  const [eventos, setEventos] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [recientes, setRecientes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [err, setErr] = useState(null);
  const [tipoFiltro, setTipoFiltro] = useState("");

  useEffect(() => {
    api.get("/v1/ani/con-eventos?dias=7")
      .then(r => setRecientes(r.data?.anis || []))
      .catch(() => setRecientes([]));
  }, []);

  const buscar = useCallback(async (numero) => {
    const n = (numero || "").trim();
    if (!n) return;
    setCargando(true); setErr(null); setBuscado(n);
    try {
      const [h, s] = await Promise.all([
        api.get(`/v1/ani/${encodeURIComponent(n)}/historial?dias=90&limit=300`),
        api.get(`/v1/ani/${encodeURIComponent(n)}/historial/resumen?dias=90`),
      ]);
      setEventos(h.data?.eventos || []);
      setResumen(s.data?.resumen || []);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
      setEventos([]); setResumen([]);
    } finally {
      setCargando(false);
    }
  }, []);

  const mostrados = tipoFiltro ? eventos.filter(e => e.tipo === tipoFiltro) : eventos;

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginTop: 0 }}>Historial de DID / ANI</h2>
      <p style={{ color: "var(--muted)", fontSize: 13, marginTop: -6 }}>
        Eventos de registro, alcanzabilidad, permisos, rutas y llamadas fallidas.
        Retención de 3 meses.
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <input className="nv-input" style={{ maxWidth: 240 }} value={ani}
               placeholder="ANI / DID (ej: 59343901992)"
               onChange={e => setAni(e.target.value)}
               onKeyDown={e => { if (e.key === "Enter") buscar(ani); }} />
        <button className="nv-btn" onClick={() => buscar(ani)} disabled={cargando}>
          {cargando ? "Buscando…" : "Ver historial"}
        </button>
        {recientes.length > 0 && (
          <select className="nv-input" style={{ maxWidth: 260 }} value=""
                  onChange={e => { if (e.target.value) { setAni(e.target.value); buscar(e.target.value); } }}>
            <option value="">— ANIs con actividad reciente —</option>
            {recientes.map(r => (
              <option key={r.ani} value={r.ani}>{r.ani} ({r.eventos} eventos)</option>
            ))}
          </select>
        )}
      </div>

      {err && <div className="nv-alert nv-alert-error" style={{ marginBottom: 12 }}>{err}</div>}

      {resumen.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
          {resumen.map(r => (
            <button key={r.tipo}
                    onClick={() => setTipoFiltro(tipoFiltro === r.tipo ? "" : r.tipo)}
                    title={`último: ${r.ultimo || "—"}`}
                    style={{
                      cursor: "pointer", border: "1px solid var(--border)", borderRadius: 8,
                      padding: "6px 10px", background: tipoFiltro === r.tipo ? "var(--border)" : "transparent",
                      borderLeft: `4px solid ${colorDe(r.tipo)}`, fontSize: 12,
                    }}>
              <b>{r.eventos}</b> {r.etiqueta}
            </button>
          ))}
          {tipoFiltro && (
            <button className="nv-btn" onClick={() => setTipoFiltro("")}>Quitar filtro</button>
          )}
        </div>
      )}

      {buscado && !cargando && eventos.length === 0 && !err && (
        <div className="nv-alert">
          Sin eventos para <b>{buscado}</b> en los últimos 90 días. Si el ANI nunca perdió el
          registro, nunca fue bloqueado y no tuvo llamadas fallidas, esto es lo esperado.
        </div>
      )}

      {mostrados.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="nv-table" style={{ minWidth: 900 }}>
            <thead>
              <tr>
                <th>Fecha y hora</th><th>Evento</th><th>Qué pasó</th>
                <th>IP pública</th><th>Dispositivo</th><th>Destino</th><th>Carrier</th><th>Q.850</th>
              </tr>
            </thead>
            <tbody>
              {mostrados.map((e, i) => (
                <tr key={`${e.ts}-${i}`}>
                  <td style={{ whiteSpace: "nowrap", fontFamily: "monospace", fontSize: 12 }}>{e.ts}</td>
                  <td style={{ borderLeft: `4px solid ${colorDe(e.tipo)}`, paddingLeft: 8, whiteSpace: "nowrap" }}>
                    {e.etiqueta}
                  </td>
                  {/* El texto lo redacta el backend (app/ani_narrativa.py); el dato crudo
                      queda en el tooltip para cuando hace falta el detalle técnico. */}
                  <td style={{ fontSize: 12, minWidth: 320 }} title={e.detalle || ""}>
                    {e.mensaje || e.detalle || "—"}
                  </td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{e.ip_publica || "—"}</td>
                  <td style={{ fontSize: 12 }}>{e.user_agent || "—"}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{e.destino || "—"}</td>
                  <td style={{ fontSize: 12 }}>{e.carrier || "—"}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{e.q850 ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
